import { RestApiClient } from "../../inpulse/RestApiClient";
import { UtilityTypeEnum, ContractTypeEnum, ProductTypeEnum, TarificationIdEnum, CustomerTypeEnum, InvoicingMethodEnum, ProcessStatusEnum, DocumentTypeEnum } from "../../inpulse/CommonEnums";
import axios, { AxiosPromise } from "axios";

export class ContractApiService {
    constructor(
        private client: RestApiClient,
        private apiUrl: string
    ) {
    }

    getMyContracts(meteringPointId: number = null, contractsInFutureAllowed: boolean = false, contractsInPastAllowed: boolean = false): Promise<IContractModel[]> {
        return this.client.get<IContractModel[]>(this.apiUrl + "contracts/mycontracts?meteringPointId=" + meteringPointId + "&contractsInFutureAllowed=" + contractsInFutureAllowed + "&contractsInPastAllowed=" + contractsInPastAllowed);
    }

    getMyContractProducts(meteringPointId: number = null, contractsInFutureAllowed: boolean = false, contractsInPastAllowed: boolean = false): Promise<IContractProductModel[]> {
        return this.client.get<IContractProductModel[]>(this.apiUrl + "products/mycontractproducts?meteringPointId=" + meteringPointId + "&contractsInFutureAllowed=" + contractsInFutureAllowed + "&contractsInPastAllowed=" + contractsInPastAllowed);
    }

    getMyContractPrices(culture: string = "fi", meteringPointId: number = null, contractsInFutureAllowed: boolean = false, contractsInPastAllowed: boolean = false): Promise<IContractPriceModel[]> {
        return this.client.get<IContractPriceModel[]>(this.apiUrl + "products/mycontractprices?culture=" + culture + "&meteringPointId=" + meteringPointId + "&contractsInFutureAllowed=" + contractsInFutureAllowed + "&contractsInPastAllowed=" + contractsInPastAllowed);
    }

    getMyContractInvoicingData(cr: IContractRef): Promise<IContractInvoicingDataModel> {
        return this.client.get<IContractInvoicingDataModel>(this.apiUrl + "contracts/invoicingdata?contractCode=" + cr.ContractCode + "&sourceCompanyCode=" + cr.SourceCompanyCode);
    }

    getInvoicingCycles(data: IInvoicingCyclePostModel): Promise<IInvoicingCycleLVM[]> {
        return this.client.get<IInvoicingCycleLVM[]>(this.apiUrl
            + "contracts/invoicingcycles"
            + this.getInvoicingCycleParams(data));
    }

    getInvoicingCycle(data: IInvoicingCyclePostModel): Promise<IInvoicingCycleLVM> {
        return this.client.get<IInvoicingCycleLVM>(this.apiUrl
            + "contracts/invoicingcycle"
            + this.getInvoicingCycleParams(data));
    }

    private getInvoicingCycleParams(data: IInvoicingCyclePostModel): string {
        return "?ContractCode=" + data.ContractCode
            + "&ContractType=" + data.ContractType
            + "&UtilityType=" + data.UtilityType
            + "&SourceCompanyCode=" + data.SourceCompanyCode
            + "&InvoicingCycleCode=";
    }

    getAttachments(contractCode: string, utilityType: UtilityTypeEnum, contractType: ContractTypeEnum, sourceCompanyCode: string): Promise<IIAttachmentModel[]> {
        return this.client.get<IIAttachmentModel[]>(this.apiUrl + "contracts/attachments?contractCode=" + contractCode + "&utilityType=" + utilityType + "&contractType=" + contractType + "&sourceCompanyCode=" + sourceCompanyCode);
    }

    getAttachment(attachment: IIAttachmentModel, contractCode: string, utilityType: UtilityTypeEnum, contractType: ContractTypeEnum, sourceCompanyCode: string): AxiosPromise {
        return axios({
            url: this.apiUrl + "contracts/attachment?attachmentId=" + attachment.AttachmentId + "&contractCode=" + contractCode + "&utilityType=" + utilityType + "&contractType=" + contractType + "&sourceCompanyCode=" + sourceCompanyCode, //your url
            method: 'GET',
            responseType: 'blob', // important
        });
    }

    saveInvoicingCycle(data: IInvoicingCyclePostModel): Promise<IInvoicingCycleValidityViewModel> {
        return this.client.post<IInvoicingCycleValidityViewModel>(this.apiUrl + "contracts/saveinvoicingcycle", data);
    }

    getMyContractProcesses(customerType: number): Promise<IContractProcessModel[]> {
        return this.client.get<IContractProcessModel[]>(this.apiUrl + "contracts/contractprocesses?customerType=" + customerType);
    }
        
    getMyContractDocumentMetaData(): Promise<ContractDocumentModel[]> {
        return this.client.get<ContractDocumentModel[]>(this.apiUrl + "contracts/mycontractdocuments");
    }

    saveContractInvoicingInformation<IContractInvoicingInformationDataModel>(data): Promise<IContractInvoicingDataModel> {
        return this.client.post<IContractInvoicingDataModel>(this.apiUrl + "contracts/saveinvoicinginformation", data);
    }

    getFollowUpOffers(): Promise<IContractProcessModel[]> {
        return this.client.get<IContractProcessModel[]>(this.apiUrl + "contracts/followupoffers");
    }

    getContractPartnersInfo(contractId: number): Promise<IContractPartnerModel[]> {
        return this.client.get<IContractPartnerModel[]>(this.apiUrl + "contracts/contractPartnersInfo?contractId=" + contractId);
    }
}

export interface IContractRef {
    ContractCode: string;
    SourceCompanyCode: string;
}

export interface IContractModel {
    Id: IContractRef;
    CISContractId: number;
    Type: ContractTypeEnum;
    Utility: UtilityTypeEnum;
    StartDate: string;
    EndDate?: string;
    FixedTermEndDate?: string;
    Customer: ICustomerModel;
    MeteringPoint: IMeteringPointModel;
    ProductsAndPriceLists: any[];
    ProductInfo: IContractProductModel;
    EnergyTaxName: string;
    TarificationId: number;
    FollowUpOfferIdentifier: string
    ContractCode: string;
    SourceCompanyCode: string;
    ContractedPower: number;
    ContractStartDate?: string;
}

export interface ICustomerModel {
    CustomerCode: string;
    SourceCompanyCode: string;
    CustomerType: CustomerTypeEnum;
    CisCustomerId: number;
    Name: string;
}

export interface IMeteringPointModel {
    MeteringPointCode: string;
    MeteringPointName: string;
    NetworkCompanyCode: string;
    SourceCompanyCode: string;
    ExternalMeteringPointCode: string;
    Address: i4U.IAddress;
    Tarification: number;
    Longitude?: number;
    Latitude?: number;
    IsConnected?: boolean;
    IsProduction?: boolean;
    CISMeteringPointId: number;
}

export interface IContractInvoicingDataModel {
    Contract: IContractRef;
    DueDate?: number;
    EstimatedBillingPeriodSteps?: number;
    InvoiceGroupCode: string;
    InvoicesPerYear?: number;
    InvoicingAddress: IInvoiceAddress;
    InvoicingMethod: InvoicingMethodEnum;
    DeliveryIban: string;
}

export interface IContractInvoicingInformationDataModel {
    Contract: IContractRef;
    InvoiceGroupCode: string;
    UtilityType: UtilityTypeEnum;
    MeteringpointId: number;
}

interface IInvoiceAddress {
    ContactPersonName: string;
    Reference: string;
    CompanyName: string;
    BusinessId: string;
    Address: any; // 
    ContactPersonEmail: string;
    ContactPersonPhone: string;
    InvoicingNetworkAddress: string; //Operaattorivälitteiset laskut, Verkkolaskuosoite (OVT-tunnus)
    InvoicingOperator: string; //Operaattorivälitteiset laskut, Operaattori
    InvoicingDeliverCode: string; //Operaattorivälitteiset laskut, Välittäjän tunnus
    InvoicingFinIbanAddress: string; //Finvoicelaskut, Verkkolaskun vastaanotto-osoite (IBAN)
    InvoicingFinDeliverCode: string; //Finvoicelaskut, Välittäjän tunnus
    InvoicingFinOvtCode: string; //Finvoicelaskut, OVT-tunnus
}

export interface IContractProductModel {
    CISContractId: number;
    Product: IProductModel;
    PriceList: IPriceListModel;
}

export interface IProductModel {
    Id: number;
    Name: string;
    Icon: string;
    Description: string;
    ProductComponents: IProductComponentModel[];
    SalesStartTime: string;
    SalesEndTime: string;
    IsSalesProduct: boolean;
    ShortDescription: string;
    Type: ProductTypeEnum;
}

export interface IPriceListModel {
    Id: number;
    Name: string;
    Name_en: string;
    Name_sv: string;
    Name_da: string;
    Description: string;
    SalesStartTime: string;
    SalesEndTime: string;
    FixedTerm: boolean;
    FixedTermDuration?: number;
    FixedTermDurationUnit?: number;
    PricingType?: number;
    ShortDescription: string;
    CampaignName: string;
    PriceListSpecialType?: number;
    MonthlyConsumptionRangeMaxValue?: number;
    MonthlyConsumptionRangeUnit: string;
}

export interface IProductComponentModel {
    Id: number;
    Code: string;
    TarificationId: TarificationIdEnum;
    Type: number;
    Description: string;
    Description_sv: string;
    Description_da: string;
    Description_en: string;
    PriceNumber: number;
    PriceString: string;
    UnitString: string;
    PriceVATPercent: string;
}

export interface IContractPriceModel {
    PriceListId: number;
    Prices: IProductComponentPriceModel[];
}

export interface IProductComponentPriceModel {
    ProductComponentId: number;
    Description: string;
    PriceWithVAT: number;
    PriceNoVAT: number;
    PriceStartDate: string;
    PriceEndDate: string;
    PriceUnit: string;
}

export enum InvoicingMethodVisualizationTypeEnum {
    NotDefined = 0,
    Dropdown = 1,
    Label = 2
}

export enum ContractValidityEnum {
    NotValid = 0,
    Valid = 1
}

export interface IContractProcessModel {
    OfferIdentifier: string;
    ContinuationContract: string;
    ProcessStatus: ProcessStatusEnum;
    OfferValidUntil?: string;
    ContractStartDate: string;
    ContractCode: string;
    SourceCompanyCode: string;
}

export class ContractDocumentModel {
    public DocumentId: string;
    public CustomerCode: string;
    public SourceCompanyCode: string;
    public ContractCode: string;
    public Mac: string;
    public StartDate: string;
    public DocumentType: DocumentTypeEnum;
    public DocumentCategory: string;
    public DocumentName: string;
}


export class IContractPartnerModel {
    public FirstName: string;
    public LastName: string;
    public CustomerCode: string;
    public IsPrimary: boolean;
}

export interface IInvoicingCycleLVM {
    InvoicingCycleCode: string;
    InvoicingCycleName: string;
}

export interface IInvoicingSaveResultModel {
    SaveSuccess: boolean;
    Message: string;
}

export interface IInvoicingCyclePostModel {
    InvoicingCycleCode: string;
    ContractCode: string;
    ContractType: ContractTypeEnum;
    UtilityType: UtilityTypeEnum;
    SourceCompanyCode: string;
}

export interface IInvoicingCycleValidityViewModel {
    InvoicingCycle: IInvoicingCycleLVM;
    StartOfOccurence: Moment;
}

export interface IIAttachmentModel {
    AttachmentId: number;
    FileName: string;
    Keywords: string[];
    Created: Moment;
}